import { useEffect, useState } from 'react';
import { Modal, Progress, Group, Button, Text, Select, Stack, ActionIcon } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload, IconX, IconPhoto } from '@tabler/icons-react';
import { uploadFile, type ImageProcessingOptions } from '@ayocore/platform/client/utils';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useUploadedFileLnkRepository, useUploadedFileRepository } from '../rpc';
import logger from '@ayocore/platform/common/helpers/logger';
import type { RepositoryCode } from '@ayocore/platform/common/types/repositories.generated';
import type { FileCategory } from '../../schemas/file-category.schema';

/**
 * Куда попадает загруженный файл (взаимоисключимо):
 * - entity-режим: привязка к сущности через uploaded_file_lnk;
 * - drive-режим: размещение в папке Диска (uploaded_files.folder_id).
 */
type FileUploadTargetProps =
  | {
      /** UUID сущности, к которой привязывается файл */
      entityUuid: string;
      /** Тип сущности — typesafe ключ из REPOSITORIES */
      entityType: RepositoryCode;
      folderId?: never;
    }
  | {
      /** Папка Диска, в которую кладётся файл */
      folderId: number;
      entityUuid?: never;
      entityType?: never;
    };

type FileUploadModalProps = FileUploadTargetProps & {
  /** Открыт ли модал */
  opened: boolean;
  /** Закрыть модал */
  onClose: () => void;
  /** Опциональная директория для организации файлов */
  directory?: string;
  /** Разрешить выбор и загрузку нескольких файлов за раз */
  multiple?: boolean;
  /** Фильтр типов в диалоге выбора (напр. 'image/*') */
  accept?: string;
  /**
   * Категории вложений точки привязки (filemanager_file_categories).
   * Если переданы — выбор категории обязателен, category_id пишется на связь.
   */
  categories?: FileCategory[];
  /** Предвыбранная категория (например, активная вкладка в EntityFiles). */
  defaultCategoryId?: number | null;
  /**
   * Жёстко заданная категория: селектор не показывается, category_id
   * проставляется молча (режим закреплённой категории в EntityFiles).
   */
  fixedCategoryId?: number;
  /** Колбэк после успешной загрузки */
  onUploaded?: () => void;
  /**
   * Файлы для предзаполнения выбора при открытии (drag-n-drop на блок EntityFiles):
   * попадают в список к загрузке, дальше — обычный флоу (категория/переименование/прогресс).
   */
  initialFiles?: File[];
  /**
   * Опциональное переименование файла перед загрузкой: возвращает целевое имя
   * (с расширением). Если не задан или вернул прежнее имя — файл грузится как есть.
   * Вызывается на каждый файл с его индексом в выбранной пачке.
   */
  renameOnUpload?: (file: File, index: number) => string;
  /**
   * Пережатие фото на сервере при загрузке (только растровые изображения):
   * ресайз по макс. стороне `width` + JPEG `quality`. Оригинал не сохраняется.
   * Не задан — файлы грузятся без обработки.
   */
  imageProcessingOptions?: ImageProcessingOptions;
}

/**
 * FileUploadModal — загрузка файла(ов) к сущности или в папку Диска.
 * Entity-режим (entityUuid + entityType): после загрузки создаёт связь через
 * UploadedFileLnkRepository. Drive-режим (folderId): проставляет
 * uploaded_files.folder_id. Проп `multiple` — выбор/загрузка пачкой
 * (последовательно, с прогрессом «N из M»); `accept` — фильтр типов в диалоге.
 */
export function FileUploadModal({
  opened,
  onClose,
  entityUuid,
  entityType,
  folderId,
  directory,
  multiple = false,
  accept,
  categories,
  defaultCategoryId,
  fixedCategoryId,
  onUploaded,
  initialFiles,
  renameOnUpload,
  imageProcessingOptions,
}: FileUploadModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  /** Индекс текущего загружаемого файла (для «N из M»). */
  const [currentIndex, setCurrentIndex] = useState(0);
  /** Текст ошибки после загрузки (есть сбойные файлы) — null, если всё чисто. */
  const [errorText, setErrorText] = useState<string | null>(null);
  /** Выбранная категория (Mantine Select оперирует строками). */
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const uploadedFileLnkRepository = useUploadedFileLnkRepository();
  const uploadedFileRepository = useUploadedFileRepository();
  // Z-index поверх дравера/модалки, из которой открыли (старый Modal вне ModalHost).
  const zIndex = useSurfaceZIndex(opened);

  /** Селектор категории нужен, когда категории есть и категория не закреплена. */
  const needsCategorySelect = (categories?.length ?? 0) > 0 && fixedCategoryId == null;

  // Предвыбор категории на каждое открытие модалки (активная вкладка могла смениться).
  useEffect(() => {
    if (opened) {
      setCategoryId(defaultCategoryId != null ? String(defaultCategoryId) : null);
    }
  }, [opened, defaultCategoryId]);

  // Предзаполнение выбора дропнутыми файлами (drag-n-drop на блок EntityFiles).
  useEffect(() => {
    if (opened && initialFiles && initialFiles.length > 0) {
      setFiles(initialFiles);
    }
  }, [opened, initialFiles]);

  // accept-строка ('image/*' / 'application/pdf,image/*') → массив mime для Dropzone;
  // пусто/не задано — undefined (принимаются любые типы).
  const acceptTypes = accept
    ?.split(',')
    .map(s => s.trim())
    .filter(Boolean);

  // Дроп/выбор файлов через Dropzone: пачкой добавляем к выбору, одиночный — заменяем.
  const handleDrop = (dropped: File[]) => {
    setFiles(prev => (multiple ? [...prev, ...dropped] : dropped.slice(0, 1)));
  };

  /** Убрать файл из выбора до загрузки. */
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const reset = () => {
    setFiles([]);
    setUploadProgress(0);
    setCurrentIndex(0);
    setErrorText(null);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setErrorText(null);
    const total = files.length;
    let linked = 0;
    // Файлы, которые не удалось загрузить/привязать — остаются в выборе для повтора
    // (успешные уже привязаны, повторять их не нужно).
    const failedFiles: File[] = [];

    try {
      for (let i = 0; i < total; i++) {
        setCurrentIndex(i);
        setUploadProgress(0);
        try {
          // Опциональное переименование перед загрузкой (имя файла = имя в хранилище).
          const original = files[i];
          const targetName = renameOnUpload?.(original, i);
          const fileToUpload =
            targetName && targetName !== original.name
              ? new File([original], targetName, {
                  type: original.type,
                  lastModified: original.lastModified,
                })
              : original;
          const result = await uploadFile(fileToUpload, {
            directory: directory?.trim() || undefined,
            onProgress: progress => setUploadProgress(progress),
            imageProcessing: imageProcessingOptions,
          });
          if (!result.success || !result.file) {
            logger.error('❌ [FileUploadModal] Upload failed:', result.error);
            failedFiles.push(files[i]);
            continue;
          }
          if (folderId != null) {
            // Drive-режим: размещаем файл в папке Диска
            await uploadedFileRepository.update(result.file.id, { folder_id: folderId });
          } else if (entityUuid != null && entityType != null) {
            // Entity-режим: привязываем файл к сущности
            const linkCategoryId =
              fixedCategoryId ?? (needsCategorySelect && categoryId ? Number(categoryId) : null);
            await uploadedFileLnkRepository.create({
              uploaded_file_id: result.file.id,
              entity_uuid: entityUuid,
              entity_type: entityType,
              ...(linkCategoryId != null ? { category_id: linkCategoryId } : {}),
            });
          } else {
            // По типам пропсов недостижимо: цель загрузки не задана
            throw new Error('FileUploadModal: не задана цель загрузки (entity или folder)');
          }
          linked++;
        } catch (error) {
          logger.error('❌ [FileUploadModal] Upload error:', error);
          failedFiles.push(files[i]);
        }
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentIndex(0);
    }

    logger.info(`[FileUploadModal] Загружено и привязано: ${linked}/${total}`);

    // Успешно привязанные уже в БД — обновляем список у потребителя.
    if (linked > 0 && onUploaded) onUploaded();

    if (failedFiles.length === 0) {
      // Полный успех — сбрасываем и закрываем.
      reset();
      onClose();
      return;
    }

    // Есть сбои: НЕ закрываем модалку, показываем ошибку, оставляем в выборе
    // только сбойные файлы — чтобы повтор не дублировал уже загруженные.
    setFiles(failedFiles);
    setErrorText(
      linked === 0
        ? `Не удалось загрузить ни одного файла (${failedFiles.length}). Проверьте файлы и попробуйте снова.`
        : `Загружено ${linked} из ${total}. Не удалось: ${failedFiles.length} — остались в списке для повтора.`
    );
  };

  const handleClose = () => {
    if (!uploading) {
      reset();
      onClose();
    }
  };

  return (
    <Modal
      zIndex={zIndex}
      opened={opened}
      onClose={handleClose}
      title={multiple ? 'Загрузить файлы' : 'Загрузить файл'}
      size='md'
      closeOnClickOutside={!uploading}
      closeOnEscape={!uploading}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {needsCategorySelect && (
          <Select
            label='Категория'
            placeholder='Выберите категорию'
            data={(categories ?? []).map(c => ({ value: String(c.id), label: c.name }))}
            value={categoryId}
            onChange={setCategoryId}
            disabled={uploading}
            required
          />
        )}

        {/* Дропзона: клик = выбор файла(ов), перетаскивание = дроп; состояния Accept/Reject/Idle. */}
        <Dropzone
          onDrop={handleDrop}
          accept={acceptTypes}
          multiple={multiple}
          disabled={uploading}
        >
          <Group justify='center' gap='md' mih={100} style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={40} color='var(--mantine-color-blue-6)' />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={40} color='var(--mantine-color-red-6)' />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={40} color='var(--mantine-color-dimmed)' />
            </Dropzone.Idle>
            <div>
              <Text size='sm' fw={500} ta='center'>
                Перетащите {multiple ? 'файлы' : 'файл'} сюда или нажмите для выбора
              </Text>
              <Text size='xs' c='dimmed' mt={4} ta='center'>
                {acceptTypes?.length ? `Допустимо: ${acceptTypes.join(', ')}` : 'Любые файлы'}
              </Text>
            </div>
          </Group>
        </Dropzone>

        {files.length > 0 && (
          <Stack gap={4}>
            {files.map((file, index) => (
              <Group key={`${file.name}-${index}`} justify='space-between' wrap='nowrap' gap='xs'>
                <Text size='sm' truncate>
                  {file.name}
                </Text>
                {!uploading && (
                  <ActionIcon
                    variant='subtle'
                    color='gray'
                    size='sm'
                    onClick={() => removeFile(index)}
                    aria-label='Убрать файл'
                  >
                    <IconX size={14} />
                  </ActionIcon>
                )}
              </Group>
            ))}
          </Stack>
        )}

        {uploading && (
          <div>
            <Text size='sm' mb='xs'>
              {files.length > 1
                ? `Загрузка ${currentIndex + 1} из ${files.length}… ${uploadProgress}%`
                : `Загрузка… ${uploadProgress}%`}
            </Text>
            <Progress value={uploadProgress} animated />
          </div>
        )}

        {errorText && !uploading && (
          <Text size='sm' c='red'>
            {errorText}
          </Text>
        )}

        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={handleClose} disabled={uploading}>
            Отмена
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || (needsCategorySelect && !categoryId) || uploading}
            loading={uploading}
          >
            Загрузить{files.length > 1 ? ` (${files.length})` : ''}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}
